import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { filter, switchMap, tap } from 'rxjs/operators';

import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import { ModuleParameterDialogComponent } from '@module-instance/module-parameter/module-parameter-dialog/module-parameter-dialog.component';
import { ModuleParamaterEnum, ModuleParameter } from '@module-instance/module-parameter/module-parameter.model';
import { ModuleParameterService } from '@module-instance/module-parameter/module-parameter.service';
import { CipoTableOptions, TableColumn } from '@shared/components/data-list/common';
import { DataListModule } from '@shared/components/data-list/data-list.module';
import { PAGE_SIZE_OPTIONS } from '@shared/consts';
import { CipoFormModule } from '@shared/modules';
import { NotificationService, UserService } from '@shared/services';
import { UserStore } from '@signal-store/index';

@Component({
  selector: 'app-module-parameter',
  templateUrl: './module-parameter.component.html',
  imports: [DataListModule, CipoFormModule],
})
export class ModuleParameterComponent implements OnInit {
  private translate = inject(TranslateService);
  private dialog = inject(MatDialog);
  private notificationService = inject(NotificationService);
  private moduleParameterService = inject(ModuleParameterService);
  private userStore = inject(UserStore);
  private userService = inject(UserService);

  private contractModuleName = computed(
    () =>
      this.userStore.menuItemsList()?.find(item => item.moduleId === SystemModulesEnum.Contracts)?.name || 'Contract',
  );

  loading = true;
  criteria = '';
  currentParameterId = ModuleParamaterEnum.agency_response_days;

  @Input('entityinstanceid') entityInstanceId: any;

  tableOptions: CipoTableOptions = {
    clickableRows: true,
    pagination: {
      pageSize: PAGE_SIZE_OPTIONS.long[3],
      pageSizeOptions: PAGE_SIZE_OPTIONS.long,
    },
    sort: {
      active: 'name',
      direction: 'asc',
    },
  };

  tableColumns: TableColumn[] = [
    {
      id: 1,
      name: 'moduleName',
      displayName: 'moduleParameter.module',
      sortable: true,
      width: 5,
    },
    {
      id: 2,
      name: 'moduleValue',
      displayName: 'moduleParameter.moduleValue',
      sortable: false,
      width: 2,
    },
    {
      id: 3,
      name: 'value',
      displayName: this.translate.instant('moduleParameter.value', { contractModuleName: this.contractModuleName() }),
      sortable: false,
      width: 2,
    },
  ];
  tableData: ModuleParameter[] = [];
  initialTableData: ModuleParameter[] = [];

  entityInstanceLocked = signal(false);
  @Input('entityinstancelocked')
  set _entityInstanceLocked(value: boolean) {
    this.entityInstanceLocked.set(value);
    this.tableOptions = { ...this.tableOptions, clickableRows: !value };
  }

  constructor() {}

  ngOnInit(): void {
    this.getMappedData();
  }

  getMappedData() {
    this.loading = true;
    this.moduleParameterService
      .getModules(this.currentParameterId, this.entityInstanceId)
      .pipe(
        tap(module => {
          this.initialTableData = module;
          this.search(this.criteria);
        }),
      )
      .subscribe(() => (this.loading = false));
  }

  search(criteria: string) {
    this.criteria = criteria;
    if (!criteria) {
      this.tableData = this.initialTableData;
      return;
    }

    this.tableData = this.initialTableData.filter(row =>
      row.moduleName.toString().toLowerCase().includes(criteria.toLowerCase()),
    );
  }

  filter(event: MatSelectChange) {
    switch (event.value) {
      case '1':
        this.tableData = this.tableData.filter(row => row.moduleOnMenu);
        break;

      default:
        this.search(this.criteria);
        break;
    }
  }

  openSettingModal(moduleParameter: ModuleParameter) {
    this.dialog
      .open(ModuleParameterDialogComponent, {
        panelClass: ['cipo-dialog', 'classic'],
        ...this.userService.getResponsiveDialogSize().sm,
        data: moduleParameter,
      })
      .afterClosed()
      .pipe(
        filter(value => value !== undefined),
        switchMap(value =>
          this.moduleParameterService.saveModuleParameter({ ...moduleParameter, value }, this.currentParameterId),
        ),
      )
      .subscribe(() => {
        this.getMappedData();
        this.notificationService.success(this.translate.instant('moduleParameter.parameterUpdated'));
      });
  }
}
